import React, { useState } from 'react';
import {
  Box,
  CardHeader,
  CardContent,
  Typography,
  Chip,
  Divider,
  Tab,
  Tabs,
  LinearProgress,
  IconButton,
  Tooltip,
  Avatar,
} from '@mui/material';
import GlassCard from '../Cards/GlassCard';
import { panelMutedSurfaceSx } from '../../utils/colors';
import CloseRoundedIcon         from '@mui/icons-material/CloseRounded';
import WhatshotRoundedIcon      from '@mui/icons-material/WhatshotRounded';
import BarChartRoundedIcon      from '@mui/icons-material/BarChartRounded';
import TimelineRoundedIcon      from '@mui/icons-material/TimelineRounded';
import PolicyRoundedIcon        from '@mui/icons-material/PolicyRounded';
import OpenInNewRoundedIcon     from '@mui/icons-material/OpenInNewRounded';
import AutoGraphRoundedIcon     from '@mui/icons-material/AutoGraphRounded';

import LeetcodeAnalyticsPanel   from './LeetcodeAnalyticsPanel';
import LeetCodeStudentAnalyticsSection from './LeetCodeStudentAnalyticsSection';

// ── Constants ─────────────────────────────────────────────────────────────────
const SUCCESS = '#2F9E44';
const WARNING = '#F59F00';
const DANGER  = '#E03131';

const TABS = [
  { key: 'analytics',    label: 'Analytics',   icon: <AutoGraphRoundedIcon sx={{ fontSize: 16 }} /> },
  { key: 'heatmap',      label: 'Activity',    icon: <WhatshotRoundedIcon  sx={{ fontSize: 16 }} /> },
  { key: 'difficulty',   label: 'Difficulty',  icon: <BarChartRoundedIcon  sx={{ fontSize: 16 }} /> },
  { key: 'timeline',     label: 'Timeline',    icon: <TimelineRoundedIcon  sx={{ fontSize: 16 }} /> },
  { key: 'plagiarism',   label: 'Plagiarism',  icon: <PolicyRoundedIcon    sx={{ fontSize: 16 }} /> },
];

// ── Difficulty bar ────────────────────────────────────────────────────────────

const DifficultyBar = ({ label, count, total, color }) => {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="caption" fontWeight={700} sx={{ color }}>
          {label}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {count} ({pct}%)
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{
          height:       8,
          borderRadius: 4,
          bgcolor:      `${color}22`,
          '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 4 },
        }}
      />
    </Box>
  );
};

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * LeetCodeStudentPanel
 *
 * An expandable analytics side-panel that appears below the LeetCode table
 * when a row is clicked. Displays 4 tabbed sections for the selected student:
 *
 *   1. Activity Heatmap       — GitHub-style calendar via LeetcodeAnalyticsPanel
 *   2. Difficulty Distribution — Easy / Medium / Hard breakdown bars
 *   3. Submission Timeline    — placeholder (no per-day data from API yet)
 *   4. Plagiarism Report      — placeholder link to existing plagiarism tooling
 *
 * @param {object}   student   — row from LeetCodeAnalyticsTable (must have profileId)
 * @param {function} onClose   — called when the × button is pressed
 */
const LeetCodeStudentPanel = ({ student, onClose }) => {
  const [activeTab, setActiveTab] = useState('analytics');

  if (!student) return null;

  const total = (student.easySolved ?? 0) + (student.mediumSolved ?? 0) + (student.hardSolved ?? 0);

  return (
    <GlassCard
      hoverable={false}
      sx={{
        height:       '100%',
        display:      'flex',
        flexDirection:'column',
        borderRadius: 0,
        border:       'none',
        borderLeft:   '3px solid',
        borderLeftColor:  'primary.main',
        overflow:     'hidden',
      }}
    >
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <CardHeader
        avatar={
          <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36, fontSize: '0.9rem' }}>
            {(student.name ?? '?')[0].toUpperCase()}
          </Avatar>
        }
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="subtitle1" fontWeight={700}>
              {student.name}
            </Typography>
            {student.studentId && (
              <Chip
                label={student.studentId}
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.7rem', height: 20 }}
              />
            )}
            {student.leetcodeUsername && (
              <Chip
                component="a"
                href={`https://leetcode.com/u/${student.leetcodeUsername}`}
                target="_blank"
                rel="noopener noreferrer"
                clickable
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                    {student.leetcodeUsername}
                    <OpenInNewRoundedIcon sx={{ fontSize: 11 }} />
                  </Box>
                }
                size="small"
                sx={{ fontSize: '0.7rem', height: 20, color: 'primary.main', borderColor: 'primary.main' }}
                variant="outlined"
              />
            )}
          </Box>
        }
        subheader={
          <Typography variant="caption" color="text.secondary">
            {student.leetcodeTotalSolved ?? 0} LeetCode problems solved
            {student.contestRating ? ` · Rating ${Math.round(student.contestRating)}` : ''}
            {student.globalRanking ? ` · Global rank #${student.globalRanking.toLocaleString()}` : ''}
          </Typography>
        }
        action={
          <Tooltip title="Close panel">
            <IconButton onClick={onClose} size="small" sx={{ mt: 0.5, mr: 0.5 }}>
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        }
        sx={{ px: 2.5, pt: 2, pb: 0 }}
      />

      {/* ── Tabs ───────────────────────────────────────────────────────────── */}
      <Box sx={{ px: 2.5, mt: 1.5 }}>
        <Tabs
          value={activeTab}
          onChange={(_e, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 36,
            borderBottom: '1px solid',
            borderColor: 'divider',
            '& .MuiTab-root': {
              minHeight:     36,
              py:            0.5,
              px:            1.5,
              fontSize:      '0.78rem',
              fontWeight:    600,
              textTransform: 'none',
              gap:           0.5,
            },
          }}
        >
          {TABS.map(({ key, label, icon }) => (
            <Tab key={key} value={key} label={label} icon={icon} iconPosition="start" />
          ))}
        </Tabs>
      </Box>

      <CardContent sx={{ flex: 1, overflowY: 'auto', px: 2.5, pt: 2, pb: 2.5, '&:last-child': { pb: 2.5 } }}>

        {/* ── 0. Analytics Dashboard ─────────────────────────────────────── */}
        {activeTab === 'analytics' && (
          <LeetCodeStudentAnalyticsSection student={student} />
        )}

        {/* ── 1. Activity Heatmap ─────────────────────────────────────────── */}
        {activeTab === 'heatmap' && (
          <LeetcodeAnalyticsPanel
            userId={student.profileId}
            title="Submission Activity"
            subheader={`${student.name}'s accepted submissions over time`}
          />
        )}

        {/* ── 2. Difficulty Distribution ──────────────────────────────────── */}
        {activeTab === 'difficulty' && (
          <Box>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
              Difficulty Breakdown
            </Typography>

            <Box sx={{ mb: 3 }}>
              <DifficultyBar
                label="Easy"
                count={student.easySolved   ?? 0}
                total={total}
                color={SUCCESS}
              />
              <DifficultyBar
                label="Medium"
                count={student.mediumSolved ?? 0}
                total={total}
                color={WARNING}
              />
              <DifficultyBar
                label="Hard"
                count={student.hardSolved  ?? 0}
                total={total}
                color={DANGER}
              />
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              {[
                { label: 'Easy',   value: student.easySolved          ?? 0, color: SUCCESS       },
                { label: 'Medium', value: student.mediumSolved         ?? 0, color: WARNING       },
                { label: 'Hard',   value: student.hardSolved           ?? 0, color: DANGER        },
                { label: 'LeetCode Total', value: student.leetcodeTotalSolved ?? 0, color: 'text.primary' },
              ].map(({ label, value, color }) => (
                <Box key={label} sx={{ textAlign: 'center', minWidth: 60 }}>
                  <Typography variant="h5" fontWeight={800} sx={{ color }}>
                    {value}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {label}
                  </Typography>
                </Box>
              ))}
              {student.acceptanceRate != null && (
                <Box sx={{ textAlign: 'center', minWidth: 60 }}>
                  <Typography variant="h5" fontWeight={800} color="primary.main">
                    {student.acceptanceRate.toFixed(1)}%
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Acceptance
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}

        {/* ── 3. Submission Timeline ───────────────────────────────────────── */}
        {activeTab === 'timeline' && (
          <Box>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
              Submission Timeline
            </Typography>

            <Box
              sx={{
                p:            3,
                borderRadius: 3,
                ...panelMutedSurfaceSx,
                border:       '1px dashed',
                borderColor:  'divider',
                textAlign:    'center',
              }}
            >
              <TimelineRoundedIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
              <Typography variant="body2" color="text.secondary" fontWeight={600}>
                Per-day timeline coming soon
              </Typography>
              <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 0.5 }}>
                Use the Activity tab to see heatmap-style submission frequency.
              </Typography>
            </Box>

            {student.avgSolveDuration != null && (
              <Box sx={{ mt: 2, p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Average solve duration
                </Typography>
                <Typography variant="body1" fontWeight={700}>
                  {student.avgSolveDuration}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* ── 4. Plagiarism Report ─────────────────────────────────────────── */}
        {activeTab === 'plagiarism' && (
          <Box>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
              Plagiarism Report
            </Typography>

            <Box
              sx={{
                p:            3,
                borderRadius: 3,
                ...panelMutedSurfaceSx,
                border:       '1px dashed',
                borderColor:  'divider',
                textAlign:    'center',
              }}
            >
              <PolicyRoundedIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
              <Typography variant="body2" color="text.secondary" fontWeight={600}>
                Plagiarism data available in Assignment Analytics
              </Typography>
              <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 0.5 }}>
                Switch to Assignment Analytics mode and open a drill-down to see
                per-student plagiarism results.
              </Typography>
            </Box>
          </Box>
        )}

      </CardContent>
    </GlassCard>
  );
};

export default LeetCodeStudentPanel;
